import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Alert, Button, Card, DatePicker, Empty, Form, Input, InputNumber, Select, Space, Spin, Statistic, Table, Tabs, Tag, Tooltip, Typography } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_DOMAIN } from '../constants';
import { useSocket } from '../contexts/SocketContext';
import useSnackbar from '../Snackbar/UseSnackbar';
import { filterTemplateHistory, friendlyOperationalLabel, historyRangeParams, TEMPLATE_MESSAGE_MODE, templateHistoryActorLabel, templateHistoryUnitPriceLabel, validateTemplatePrice } from './templateMessageModel';

const { RangePicker } = DatePicker;
const EMPTY_FILTERS = { actor: '', template: '', recipient: '', source: '', status: '', channel: '' };
const responseData = response => {
  const data = response.data?.data ?? {};
  if (typeof data !== 'string') return data;
  try { return JSON.parse(data); } catch (_) { return {}; }
};
const statusColors = {
  attempting: 'processing',
  provider_accepted: 'gold',
  sent: 'cyan',
  delivered: 'green',
  read: 'purple',
  failed: 'red',
};
const dateTimeLabel = value => value ? new Date(value).toLocaleString() : '—';
const failureLabel = row => row.failed_detail || row.last_error;
const historyColumns = [
  { title: 'Attempted', dataIndex: 'attempted_at', render: dateTimeLabel },
  { title: 'Last status', dataIndex: 'last_status_at', render: dateTimeLabel },
  { title: 'Agent', render: (_, row) => templateHistoryActorLabel(row) },
  { title: 'Query', render: (_, row) => row.query_id || row.query_pk || '—' },
  { title: 'Template', dataIndex: 'template_name' },
  { title: 'Recipient', dataIndex: 'recipient_phone' },
  { title: 'Source', dataIndex: 'source', render: value => <span title={value}>{friendlyOperationalLabel(value)}</span> },
  { title: 'Status', dataIndex: 'status', render: value => <Tag color={statusColors[value] || 'default'}>{friendlyOperationalLabel(value)}</Tag> },
  { title: 'Billable', dataIndex: 'is_billable', render: value => <Tag color={value ? 'green' : 'default'}>{value ? 'Yes' : 'No'}</Tag> },
  { title: 'Unit price', render: (_, row) => { const label = templateHistoryUnitPriceLabel(row); return <span aria-label={`Unit price ${label}`}>{label}</span>; } },
  { title: 'WATI channel', dataIndex: 'channel_id', render: value => value || '—' },
  { title: 'WATI message', dataIndex: 'wati_message_id', render: value => value || '—' },
  { title: 'WhatsApp message', dataIndex: 'whatsapp_message_id', render: value => value || '—' },
  { title: 'Local message', dataIndex: 'local_message_id', render: value => value || '—' },
  { title: 'Billable at', dataIndex: 'billable_at', render: dateTimeLabel },
  { title: 'API completed', dataIndex: 'completed_at', render: dateTimeLabel },
  { title: 'Failure', render: (_, row) => failureLabel(row) ? <Typography.Text type="danger">{row.failed_code ? `[${row.failed_code}] ` : ''}{failureLabel(row)}</Typography.Text> : '—' },
];

const contentWidth = (options, placeholder) => ({ '--template-filter-width': `calc(${Math.max(placeholder.length, ...options.map(option => String(option.label).length))}ch + 44px)` });

export function TemplateMessageHistoryResults({ history, filters = EMPTY_FILTERS, onFilter = () => {}, loading = false, tableScroll = { x: 2300 }, controls = null }) {
  const rows = history.rows || [], summary = history.summary;
  const filteredRows = filterTemplateHistory(rows, filters);
  const distinctOptions = (values, label = value => value) => [...new Set(values.filter(value => value !== null && value !== undefined && value !== ''))]
    .sort((left, right) => String(label(left)).localeCompare(String(label(right))))
    .map(value => ({ value: String(value), label: label(value) }));
  const actorOptions = [...new Map(rows.map(row => {
    const value = row.actor_name || (row.actor_user_id ? String(row.actor_user_id) : 'Unknown agent');
    return [value, { value, label: templateHistoryActorLabel(row) }];
  })).values()].sort((left, right) => left.label.localeCompare(right.label));
  const templateOptions = distinctOptions(rows.map(row => row.template_name));
  const recipientOptions = distinctOptions(rows.map(row => row.recipient_phone));
  const sourceOptions = distinctOptions(rows.map(row => row.source), friendlyOperationalLabel);
  const statusOptions = distinctOptions(rows.map(row => row.status), friendlyOperationalLabel);
  const channelOptions = distinctOptions(rows.map(row => row.channel_id));
  return <>
    {summary && <div className="template-message-summary">
      <Card><Statistic title="Total attempts" value={summary.attempts || 0} /></Card>
      <Card><Statistic title="Awaiting provider status" value={summary.awaiting_provider_status || 0} /></Card>
      <Card><Statistic title="Sent" value={summary.sent || 0} /></Card>
      <Card><Statistic title="Delivered" value={summary.delivered || 0} /></Card>
      <Card><Statistic title="Read" value={summary.read || 0} /></Card>
      <Card><Statistic title="Failed" value={summary.failed || 0} /></Card>
      <Card><Statistic title="Billable" value={summary.billable || 0} /></Card>
      <Card aria-label={`Estimated spending ${Number(summary.estimated_spend || 0).toFixed(4)} ${summary.currency || 'USD'}`}><Statistic title="Estimated spending" value={summary.estimated_spend || 0} precision={4} suffix={summary.currency || 'USD'} /></Card>
    </div>}
    <div className="template-message-filters">
      <Select className="template-message-filter" style={contentWidth(actorOptions, 'Agent')} aria-label="Filter agent" showSearch optionFilterProp="label" allowClear placeholder="Agent" value={filters.actor || undefined} onChange={value => onFilter('actor', value || '')} options={actorOptions} />
      <Select className="template-message-filter" style={contentWidth(templateOptions, 'Template name')} aria-label="Filter template" showSearch optionFilterProp="label" allowClear placeholder="Template name" value={filters.template || undefined} onChange={value => onFilter('template', value || '')} options={templateOptions} />
      <Select className="template-message-filter" style={contentWidth(recipientOptions, 'Recipient phone')} aria-label="Filter recipient" showSearch optionFilterProp="label" allowClear placeholder="Recipient phone" value={filters.recipient || undefined} onChange={value => onFilter('recipient', value || '')} options={recipientOptions} />
      <Select className="template-message-filter" style={contentWidth(sourceOptions, 'Source')} aria-label="Filter source" showSearch optionFilterProp="label" allowClear placeholder="Source" value={filters.source || undefined} onChange={value => onFilter('source', value || '')} options={sourceOptions} />
      <Select className="template-message-filter" style={contentWidth(statusOptions, 'Status')} aria-label="Filter status" showSearch optionFilterProp="label" allowClear placeholder="Status" value={filters.status || undefined} onChange={value => onFilter('status', value || '')} options={statusOptions} />
      <Select className="template-message-filter" style={contentWidth(channelOptions, 'WATI channel')} aria-label="Filter WATI channel" showSearch optionFilterProp="label" allowClear placeholder="WATI channel" value={filters.channel || undefined} onChange={value => onFilter('channel', value || '')} options={channelOptions} />
      {controls}
    </div>
    {summary && !loading && rows.length === 0 ? <Empty description="No template message sends in this date range" /> : filteredRows.length === 0 && rows.length > 0 ? <Empty description="No sends match the selected filters" /> : <Table rowKey="id" size="small" loading={loading} dataSource={filteredRows} columns={historyColumns} pagination={{ defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: [50, 100, 200], showTotal: total => `${total} sends` }} scroll={tableScroll || undefined} />}
  </>;
}

export function TemplateMessageManagementPanel() {
  const { socket } = useSocket();
  const { SuccessSnackbar, ErrorSnackbar } = useSnackbar();
  const errorSnackbarRef = useRef(ErrorSnackbar);
  errorSnackbarRef.current = ErrorSnackbar;
  const headers = useMemo(() => ({ accessToken: localStorage.getItem('accessToken') }), []);
  const [form] = Form.useForm();
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [range, setRange] = useState(() => { const today = dayjs(); return [today.startOf('day'), today.endOf('day')]; });
  const initialRange = useRef(range);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [history, setHistory] = useState({ rows: [], summary: null });
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const response = await axios.post(`${API_DOMAIN}/whatsapp/get_misc_mode`, { mode: TEMPLATE_MESSAGE_MODE }, { headers });
      const data = responseData(response);
      form.setFieldsValue({ price_per_message: data.price_per_message, currency: data.currency || 'AED' });
    } catch (error) {
      errorSnackbarRef.current(error.response?.data?.message || error.message || 'Unable to load template message settings.');
    } finally {
      setSettingsLoading(false);
    }
  }, [form, headers]);

  const fetchHistory = useCallback(async selectedRange => {
    let params;
    try { params = historyRangeParams(selectedRange); }
    catch (error) { setHistoryError(error.message); return; }
    setHistoryLoading(true); setHistoryError(''); setFilters(EMPTY_FILTERS);
    try {
      const response = await axios.get(`${API_DOMAIN}/wati_messages/template-message-sends`, { params, headers });
      const data = responseData(response);
      setHistory({ rows: Array.isArray(data.rows) ? data.rows : [], summary: data.summary || null });
    } catch (error) {
      setHistoryError(error.response?.data?.message || error.message || 'Unable to load send history.');
    } finally { setHistoryLoading(false); }
  }, [headers]);

  useEffect(() => { loadSettings(); fetchHistory(initialRange.current); }, [fetchHistory, loadSettings]);
  useEffect(() => {
    if (!socket) return undefined;
    const onConfigUpdated = payload => { if (payload?.mode === TEMPLATE_MESSAGE_MODE) loadSettings(); };
    socket.on('misc_config_updated', onConfigUpdated);
    return () => socket.off('misc_config_updated', onConfigUpdated);
  }, [loadSettings, socket]);

  const saveSettings = async values => {
    let price;
    try { price = validateTemplatePrice(values.price_per_message); }
    catch (error) { form.setFields([{ name: 'price_per_message', errors: [error.message] }]); return; }
    setSaving(true);
    try {
      const response = await axios.post(`${API_DOMAIN}/whatsapp/save_misc_mode`, {
        mode: TEMPLATE_MESSAGE_MODE,
        moredetails: { price_per_message: price, currency: values.currency },
      }, { headers });
      if (!response.data?.success) throw new Error(response.data?.message || 'Unable to save template message settings.');
      SuccessSnackbar('Template message settings saved.');
    } catch (error) {
      ErrorSnackbar(error.response?.data?.message || error.message);
    } finally { setSaving(false); }
  };

  return <div className="template-message-management settings-panel-fill">
    <Tabs className="settings-fill-tabs" items={[
      { key: 'settings', label: 'Settings', children: <Spin spinning={settingsLoading}><Form form={form} layout="vertical" onFinish={saveSettings} style={{ maxWidth: 420 }}>
        <Form.Item name="price_per_message" label="Price per message" rules={[{ validator: (_, value) => { try { validateTemplatePrice(value); return Promise.resolve(); } catch (error) { return Promise.reject(error); } } }]}>
          <InputNumber min={0} max={100000} precision={4} step={0.0001} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="currency" label="Currency" rules={[{ required: true }]}><Input readOnly /></Form.Item>
        <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
      </Form></Spin> },
      { key: 'history', label: 'Send History', children: <div className="template-message-history">
        {historyError && <Alert showIcon type="error" message={historyError} />}
        <TemplateMessageHistoryResults history={history} filters={filters} loading={historyLoading} onFilter={(key, value) => setFilters(current => ({ ...current, [key]: value }))} controls={<Space className="template-message-date-controls"><RangePicker className="template-message-date-range" showTime={{ format: 'HH:mm' }} format="YYYY-MM-DD HH:mm" value={range} onChange={setRange} /><Tooltip title="Load history"><Button type="primary" aria-label="Load history" icon={<SearchOutlined />} loading={historyLoading} onClick={() => fetchHistory(range)} /></Tooltip></Space>} />
      </div> },
    ]} />
  </div>;
}
